import { useState } from 'react';
import { 
  Dialog, DialogTitle, DialogContent, DialogActions, 
  Button, TextField, Typography, Box, LinearProgress 
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { api, authenticatedFetch } from '../utils/api';

function GoalProgressUpdate({ open, onClose, goal, onUpdate }) {
  const [currentValue, setCurrentValue] = useState(goal?.current?.toString() || '');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!currentValue || isNaN(currentValue)) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(api.goalProgress(goal._id), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          current: Number(currentValue),
          status: Number(currentValue) >= goal.target ? 'completed' : 'active'
        })
      });

      if (response.ok) {
        const updatedGoal = await response.json();
        onUpdate(updatedGoal);
        onClose();
      }
    } catch (error) {
      console.error('Error updating goal progress:', error);
    } finally {
      setLoading(false);
    }
  };

  const getProgress = () => {
    if (!goal) return 0;
    return Math.min(100, Math.max(0, (goal.current / goal.target) * 100));
  };

  const getNewProgress = () => {
    if (!currentValue || isNaN(currentValue)) return getProgress();
    return Math.min(100, Math.max(0, (Number(currentValue) / goal.target) * 100));
  };

  if (!goal) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <TrendingUpIcon color="primary" />
        Update Progress: {goal.title}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Current Progress: {goal.current} / {goal.target}
          </Typography>
          <LinearProgress 
            variant="determinate" 
            value={getProgress()} 
            sx={{ height: 8, borderRadius: 4, mb: 1 }}
          />
          <Typography variant="caption" color="text.secondary">
            {Math.round(getProgress())}% complete
          </Typography>
        </Box>

        <TextField
          fullWidth
          label="New Current Value"
          type="number"
          value={currentValue}
          onChange={(e) => setCurrentValue(e.target.value)}
          helperText={`Target: ${goal.target}`}
          sx={{ mb: 2 }}
        />

        {currentValue && !isNaN(currentValue) && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              New Progress: {Number(currentValue)} / {goal.target}
            </Typography>
            <LinearProgress 
              variant="determinate" 
              value={getNewProgress()} 
              sx={{ height: 8, borderRadius: 4, mb: 1 }}
              color={getNewProgress() >= 100 ? 'success' : 'primary'}
            />
            <Typography variant="caption" color="text.secondary">
              {Math.round(getNewProgress())}% complete
            </Typography>
            {getNewProgress() >= 100 && (
              <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 1 }}>
                🎉 Goal will be marked as completed!
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={loading || !currentValue || isNaN(currentValue)}
        >
          {loading ? 'Updating...' : 'Update Progress'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default GoalProgressUpdate;

